/** Wiederverwendete Bausteine der Oberflaeche. */

import type { ReactNode } from "react";
import type { FractionVote, Initiator } from "./api";

/** Parteikennzeichnung: Farbfeld plus Kuerzel.
 *
 * Das Kuerzel steht immer daneben. Parteifarben sind vorgegeben und teils
 * schlecht unterscheidbar -- Schwarz hat keine Buntheit, Gelb kaum Kontrast --
 * deshalb darf die Farbe die Identitaet nie allein tragen.
 */
export function PartyTag({
  short,
  color,
}: {
  short: string | null;
  color?: string | null;
}) {
  if (!short) return <span className="party">fraktionslos</span>;
  return (
    <span className="party">
      <i className="swatch" style={{ background: color ?? "var(--border-strong)" }} />
      {short}
    </span>
  );
}

/** Urheber einer Vorlage.
 *
 * Fraktionen werden mit Farbfeld und Kuerzel gezeigt, alles andere im
 * Klartext: bei einem Regierungsentwurf ist der Urheber die Bundesregierung
 * oder ein Ministerium, nicht eine Partei. Das ist eine Aussage über die
 * Herkunft der Vorlage und soll nicht zu einer Partei verkürzt werden.
 */
export function Initiators({
  urheber,
  fallback = "nicht angegeben",
}: {
  urheber: Initiator[] | null | undefined;
  fallback?: string;
}) {
  if (!urheber || urheber.length === 0) {
    return <span style={{ color: "var(--ink-3)" }}>{fallback}</span>;
  }
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "0.45rem" }}>
      {urheber.map((u) =>
        u.short_name ? (
          <PartyTag key={u.name} short={u.short_name} color={u.color} />
        ) : (
          <span key={u.name} style={{ fontSize: "0.85rem" }}>
            {u.name}
          </span>
        ),
      )}
    </span>
  );
}

/** Einordnung: wessen Angebot diese Seite ist.
 *
 * Steht auf Impressum und Datenschutzerklärung. Macht die Zugehörigkeit zum
 * übrigen Angebot des Betreibers deutlich, ohne die projektspezifischen Teile
 * dieser Seiten auf eine fremde Domain auszulagern — die Benennung des
 * Verantwortlichen nach § 18 Absatz 2 MStV und die Hinweise zu den Bewertungen
 * gelten für PolitWatch, nicht für das Hauptangebot.
 */
export function BetreiberHinweis({
  betreiber,
  hauptseite,
}: {
  betreiber: string;
  hauptseite: string;
}) {
  if (!betreiber) return null;
  return (
    <p style={{ color: "var(--ink-2)", fontSize: "0.92rem", marginTop: "0.6rem" }}>
      PolitWatch ist ein Angebot der {betreiber}.
      {hauptseite ? (
        <>
          {" "}
          Weitere Angebote unter{" "}
          <a href={hauptseite} target="_blank" rel="noreferrer noopener">
            {hauptseite.replace(/^https?:\/\//, "")}
          </a>
          .
        </>
      ) : null}
    </p>
  );
}

export function Tile({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note ? <div className="note">{note}</div> : null}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  action,
  children,
}: {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {title ? (
        <div className="card-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: "0.6rem" }} aria-busy="true" aria-live="polite">
      <span className="sr-only" style={{ position: "absolute", left: -9999 }}>
        Daten werden geladen
      </span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 7}%` }} />
      ))}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="notice error" role="alert">
      <strong>Daten konnten nicht geladen werden.</strong> {message}
      {onRetry ? (
        <>
          {" "}
          <button onClick={onRetry} style={{ marginLeft: "0.5rem" }}>
            Erneut versuchen
          </button>
        </>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** Wandelt HTML aus einer externen Quelle in reinen Text.
 *
 * Der Beschreibungstext kommt von abgeordnetenwatch und enthaelt Markup.
 * Er wird bewusst NICHT als HTML eingesetzt: fremdes Markup in die eigene
 * Seite zu haengen, oeffnet die Tuer fuer eingebetteten Schadcode. DOMParser
 * liest das Dokument, ohne Skripte auszufuehren; uebrig bleibt der Text --
 * korrekt dekodierte Sonderzeichen inklusive.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
  } catch {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "–";
  return value.toLocaleString("de-DE");
}

/** Gestapelter Balken für das Stimmverhalten einer Fraktion.
 *
 * Abwesende tragen zusaetzlich eine Schraffur: als vierte Kategorie waeren
 * sie in reiner Graustufe nicht sicher von der Enthaltung zu unterscheiden.
 */
export function VoteStack({ row }: { row: FractionVote }) {
  const total = row.n_total || 1;
  const segments: Array<[string, number, string]> = [
    ["seg-yes", row.n_yes, "Ja"],
    ["seg-no", row.n_no, "Nein"],
    ["seg-abstain", row.n_abstain, "Enthaltung"],
    ["seg-absent", row.n_no_show, "nicht abgestimmt"],
  ];
  const title = segments
    .filter(([, n]) => n > 0)
    .map(([, n, label]) => `${label}: ${n}`)
    .join(" · ");

  return (
    <div className="stack" title={title} role="img" aria-label={title}>
      {segments.map(([cls, n]) =>
        n > 0 ? (
          <span key={cls} className={cls} style={{ width: `${(n / total) * 100}%` }} />
        ) : null,
      )}
    </div>
  );
}

export function VoteLegend() {
  return (
    <div className="legend">
      <span className="key">
        <i className="yes" /> Ja
      </span>
      <span className="key">
        <i className="no" /> Nein
      </span>
      <span className="key">
        <i className="abstain" /> Enthaltung
      </span>
      <span className="key">
        <i className="absent" /> nicht abgestimmt
      </span>
    </div>
  );
}

/** Waagerechter Balken für einen einzelnen Wert. */
export function Bar({
  label,
  value,
  max,
  color,
  suffix,
  title,
}: {
  label: ReactNode;
  value: number;
  max: number;
  color?: string | null;
  suffix?: string;
  /** Vollstaendiger Text, falls das Label in der Spalte abgeschnitten wird. */
  title?: string;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="bar-row">
      <div
        title={title}
        style={{
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div className="bar-track">
        <div
          className="bar-fill"
          style={{ width: `${pct}%`, background: color ?? "var(--accent)" }}
        />
      </div>
      <div className="bar-value">
        {formatNumber(value)}
        {suffix ?? ""}
      </div>
    </div>
  );
}

export function Pager({
  offset,
  limit,
  total,
  onChange,
}: {
  offset: number;
  limit: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="pager">
      <button disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        Zurück
      </button>
      <span>
        {formatNumber(from)}–{formatNumber(to)} von {formatNumber(total)}
      </span>
      <button disabled={to >= total} onClick={() => onChange(offset + limit)}>
        Weiter
      </button>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  erfuellt: "erfüllt",
  teilweise_erfuellt: "teilweise erfüllt",
  in_arbeit: "in Arbeit",
  gebrochen: "gebrochen",
  nicht_angegangen: "nicht angegangen",
  nicht_bewertbar: "nicht bewertbar",
};

const STATUS_COLOR: Record<string, string> = {
  erfuellt: "var(--status-erfuellt)",
  teilweise_erfuellt: "var(--status-teilweise)",
  in_arbeit: "var(--status-arbeit)",
  gebrochen: "var(--status-gebrochen)",
  nicht_angegangen: "var(--status-offen)",
  nicht_bewertbar: "var(--border-strong)",
};

export function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="pill">unbewertet</span>;
  return (
    <span
      className="badge"
      style={{ borderColor: STATUS_COLOR[status], color: STATUS_COLOR[status] }}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export const COMMITMENT_LABEL: Record<string, string> = {
  fest: "feste Zusage",
  absicht: "Absicht",
  pruefauftrag: "Prüfauftrag",
  vage: "vage",
};

export const DIRECTION_LABEL: Record<string, string> = {
  erfuellt: "spricht dafür",
  teilweise: "spricht teilweise dafür",
  widerspricht: "spricht dagegen",
  neutral: "neutral",
};

/** Richtung eines Versprechens. In der Datenbank stehen ASCII-Bezeichner,
 *  weil sie technische Werte sind — angezeigt wird deutscher Text. */
export const PROMISE_DIRECTION_LABEL: Record<string, string> = {
  einfuehren: "einführen",
  abschaffen: "abschaffen",
  erhoehen: "erhöhen",
  senken: "senken",
  beibehalten: "beibehalten",
  sonstige: "sonstige",
};
